import React, { useEffect, useMemo, useState } from 'react'
import {
	Dialog,
	TextField,
	DialogTitle,
	DialogActions,
	DialogContent,
} from '@mui/material'
import {
	SiteConfiguration,
	CreateInviteRequest,
} from '@matech/thebigpos-sdk'
import { useVenti } from 'venti'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import CloseIcon from '@mui/icons-material/Close'

import {
	isEmailValid,
	isPhoneNumberValid,
	borrowerRelationships,
} from '../../services/helper'
import en from '../../i18n/en'
import es from '../../i18n/es'
import { useAlert } from '../../hooks'
import { getTheme } from '../../config'
import { Roles } from '../../services/client'
import IconButton from '../Button/IconButton'
import { Button, LoadingBtn } from '../Button'
import { applyMask } from '../../services/utils'
import queryKeys from '../../services/queryKeys'
import { Locale, defaultLocale } from '../../i18n'
import { useMixpanel } from '../../hooks/useMixpanel'
import { eventTypes } from '../../services/constants'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { useLoanContext } from '../../contexts/LoanContext'
import { navigationLinking } from '../../services/navigation'

const theme = getTheme()

const localeMap = {
	en,
	es,
}

type RouteMap = {
	borrower: string
	nonBorrower: string
	invite: string
}

const localeRouteMap: Record<Locale, RouteMap> = {
	en: {
		borrower: 'ApplyCoBorrower',
		nonBorrower: 'LOApplyCoBorrower',
		invite: 'InviteApply',
	},
	es: {
		borrower: 'ApplyCoBorrowerSpanish',
		nonBorrower: 'LOApplyCoBorrowerSpanish',
		invite: 'InviteApplySpanish',
	},
}

const CO_BORROWERS_LIMIT = 1

export type ModalAddCoBorrowerProps = {
	role: string
	visible: boolean
	setVisible?: (value: boolean) => void
	siteConfig: SiteConfiguration | null
	blacklist?: string[]
	locale?: Locale
	onClose?: () => void
}

const ModalAddCoBorrower = ({
	role,
	visible,
	setVisible,
	onClose,
	siteConfig,
	blacklist = [],
	locale = defaultLocale,
}: ModalAddCoBorrowerProps) => {
	const navigate = useNavigate()
	const ventiState = useVenti()
	const { alert } = useAlert()
	const mixpanel = useMixpanel()
	const { loanId } = useLoanContext()

	const coBorrowerRoute =
		ventiState.get(theme.storageKeys.coBorrowerRoute) ||
		Roles.borrower === role
			? localeRouteMap[locale].borrower
			: localeRouteMap[locale].nonBorrower

	const [display, setDisplay] = useState('method')
	const [sending, setSending] = useState(false)
	const [formData, setFormData] = useState<CreateInviteRequest>({
		firstName: '',
		lastName: '',
		emailAddress: '',
		phoneNumber: '',
		siteConfigurationID: siteConfig?.id ?? '',
		relationship: 'NotApplicable',
		loanID: '',
	})

	const [validationMessages, setValidationMessages] = useState({
		firstName: '',
		lastName: '',
		emailAddress: '',
		phoneNumber: '',
	})

	const { data: loan } = useQuery({
		queryKey: [queryKeys.loan, loanId],
		queryFn: () => TheBigPOSApi.getLoan(loanId),
		enabled: !!loanId,
	})

	const maximumCoBorrowersReached = useMemo(() => {
		if (!loan?.data?.userLoans) return false

		const coBorrowersArray =
			loan.data.userLoans.filter(
				(user) => user.role === 'CoBorrower'
			) || []

		return coBorrowersArray.length >= CO_BORROWERS_LIMIT
	}, [loan])

	const updateData = (
		field: keyof CreateInviteRequest,
		value: string
	) => {
		setValidationMessages((messages) => ({
			...messages,
			[field]: '',
		}))
		setFormData((data) => ({ ...data, [field]: value }))
	}

	const handleClose = () => {
		setDisplay(
			maximumCoBorrowersReached ? 'borrowerLimitReached' : 'method'
		)
		setVisible?.(false)
		onClose?.()
	}

	const handleNavigateToRoute = () => {
		handleClose()
		setTimeout(
			() =>
				navigate(
					`/${navigationLinking[coBorrowerRoute as keyof typeof navigationLinking]}`
				),
			50
		)
		mixpanel.trackEvent(eventTypes.ADD_BORROWER_MANUALLY, {
			loanId,
		})
	}

	const handleBlur = (field: keyof CreateInviteRequest) => {
		if (field === 'firstName' && formData.firstName === '') {
			setValidationMessages((messages) => ({
				...messages,
				firstName:
					localeMap[locale].validation.name.first_name_required,
			}))
		} else if (field === 'lastName' && formData.lastName === '') {
			setValidationMessages((messages) => ({
				...messages,
				lastName:
					localeMap[locale].validation.name.last_name_required,
			}))
		} else if (field === 'emailAddress') {
			if (formData.emailAddress === '')
				setValidationMessages((messages) => ({
					...messages,
					emailAddress: localeMap[locale].validation.email.required,
				}))
			else if (!isEmailValid(formData.emailAddress))
				setValidationMessages((messages) => ({
					...messages,
					emailAddress: localeMap[locale].validation.email.invalid,
				}))
			else if (blacklist.includes(formData.emailAddress))
				setValidationMessages((messages) => ({
					...messages,
					emailAddress:
						localeMap[locale].validation.email.unique_borrower,
				}))
			else
				setValidationMessages((messages) => ({
					...messages,
					emailAddress: '',
				}))
		} else if (field === 'phoneNumber' && formData.phoneNumber) {
			if (formData.phoneNumber === '')
				setValidationMessages((messages) => ({
					...messages,
					phoneNumber: localeMap[locale].validation.phone.required,
				}))
			else if (!isPhoneNumberValid(formData.phoneNumber))
				setValidationMessages((messages) => ({
					...messages,
					phoneNumber: localeMap[locale].validation.phone.invalid,
				}))
		}
	}

	const handleRelationshipPress = (
		val: CreateInviteRequest['relationship']
	) => {
		updateData('relationship', val)
		setDisplay('submit')
	}

	const onBack = () => {
		if (display === 'invite') {
			setDisplay('method')
		}

		if (display === 'relationship') {
			setDisplay('invite')
		}

		if (display === 'submit') {
			setDisplay('relationship')
		}
	}

	const sendInvite = async () => {
		setSending(true)
		try {
			const { emailAddress } = formData
			await TheBigPOSApi.inviteUser({
				...formData,
				loanID: loanId,
				loanRole: 'CoBorrower',
				route: localeRouteMap[locale].invite,
			})
			handleClose()
			setSending(false)
			mixpanel.trackEvent(eventTypes.ADD_BORROWER_BY_INVITATION, {
				loanId,
				email: emailAddress,
			})
			alert(
				`${localeMap[locale].alerts.invite_sent_to} ${formData.firstName} ${formData.lastName} <${emailAddress}>!`
			)
		} catch {
			setSending(false)
			alert(localeMap[locale].errors.general, { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				localeMap[locale].errors.general
			)
		}
	}

	useEffect(() => {
		if (maximumCoBorrowersReached) {
			setDisplay('borrowerLimitReached')
		}
	}, [loan, maximumCoBorrowersReached])

	return (
		<Dialog open={visible} aria-labelledby="responsive-dialog-title">
			<DialogTitle sx={{ m: 0, p: 2 }}>
				{localeMap[locale].add_coborrower_form.modal_heading}
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{display === 'borrowerLimitReached' && (
					<div style={{ textAlign: 'center', marginBottom: 20 }}>
						{
							localeMap[locale].add_coborrower_form
								.max_borrowers_reached
						}
					</div>
				)}
				{display === 'method' && (
					<>
						<div style={{ textAlign: 'center', marginBottom: 20 }}>
							{localeMap[locale].add_coborrower_form.method_heading}
						</div>
						<div
							className="w-9/12 text-center"
							style={{ margin: '0 auto' }}
						>
							<div className="mb-4">
								<Button
									style={{ width: '100%' }}
									text={
										localeMap[locale].add_coborrower_form
											.send_invitation
									}
									variant="outlined"
									onClick={() => setDisplay('invite')}
								/>
							</div>
							<div>
								<Button
									style={{ width: '100%' }}
									text={
										localeMap[locale].add_coborrower_form
											.provide_information
									}
									variant="outlined"
									onClick={handleNavigateToRoute}
								/>
							</div>
						</div>
					</>
				)}

				{display === 'invite' && (
					<div style={{ maxWidth: '400px' }}>
						<div style={{ textAlign: 'center' }}>
							{localeMap[locale].add_coborrower_form.invite_heading}
						</div>
						<TextField
							className="w-full"
							sx={{ mb: 1 }}
							variant="standard"
							placeholder={
								localeMap[locale].create_account.first_name
							}
							value={formData.firstName}
							onChange={(e) =>
								updateData('firstName', e.target.value)
							}
							label={localeMap[locale].create_account.first_name}
							onBlur={() => handleBlur('firstName')}
							error={!!validationMessages.firstName}
							helperText={
								validationMessages.firstName
									? validationMessages.firstName
									: ''
							}
						/>

						<TextField
							className="w-full"
							sx={{ mb: 1 }}
							variant="standard"
							placeholder={localeMap[locale].create_account.last_name}
							value={formData.lastName}
							onChange={(e) => updateData('lastName', e.target.value)}
							label={localeMap[locale].create_account.last_name}
							onBlur={() => handleBlur('lastName')}
							error={!!validationMessages.lastName}
							helperText={
								validationMessages.lastName
									? validationMessages.lastName
									: ''
							}
						/>

						<TextField
							className="w-full"
							sx={{ mb: 1 }}
							variant="standard"
							placeholder={localeMap[locale].global.email}
							value={formData.emailAddress}
							onChange={(e) =>
								updateData('emailAddress', e.target.value)
							}
							label={localeMap[locale].global.email}
							onBlur={() => handleBlur('emailAddress')}
							error={!!validationMessages.emailAddress}
							helperText={
								validationMessages.emailAddress
									? validationMessages.emailAddress
									: ''
							}
						/>

						<TextField
							className="w-full"
							sx={{ mb: 1 }}
							variant="standard"
							placeholder={localeMap[locale].global.phone_number}
							value={formData.phoneNumber}
							onChange={(e) =>
								updateData(
									'phoneNumber',
									applyMask(e.target.value, 'phone')
								)
							}
							label={localeMap[locale].global.phone_number}
							onBlur={() => handleBlur('phoneNumber')}
							error={!!validationMessages.phoneNumber}
							helperText={
								validationMessages.phoneNumber
									? validationMessages.phoneNumber
									: ''
							}
						/>
					</div>
				)}

				{display === 'relationship' && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<div className="mb-4" style={{ textAlign: 'center' }}>
							{localeMap[
								locale
							].add_coborrower_form.relationship.replace(
								'{name}',
								formData.firstName
							)}
						</div>
						<div
							className="w-9/12 text-center"
							style={{ margin: '0 auto' }}
						>
							<div className="mb-4">
								<Button
									style={{ width: '100%' }}
									text={localeMap[locale].global.yes}
									className="mb-2"
									variant={
										formData.relationship === borrowerRelationships[1]
											? 'contained'
											: 'outlined'
									}
									onClick={() =>
										handleRelationshipPress(
											borrowerRelationships[1] as CreateInviteRequest['relationship']
										)
									}
								/>
							</div>
							<div>
								<Button
									style={{ width: '100%' }}
									text={localeMap[locale].global.no}
									variant={
										formData.relationship === borrowerRelationships[2]
											? 'contained'
											: 'outlined'
									}
									onClick={() =>
										handleRelationshipPress(
											borrowerRelationships[2] as CreateInviteRequest['relationship']
										)
									}
								/>
							</div>
						</div>
					</div>
				)}

				{display === 'submit' && (
					<>
						<div
							style={{ marginBottom: 20 }}
							className="text-sm md:text-base"
						>
							{
								localeMap[locale].add_coborrower_form.disclaimer
									.heading
							}
						</div>
						<div className="flex mb-1 text-sm md:text-base">
							<div className="font-bold">
								{localeMap[locale].create_account.first_name}:&nbsp;
							</div>
							<div>{formData.firstName}</div>
						</div>
						<div className="flex mb-1 text-sm md:text-base">
							<div className="font-bold">
								{localeMap[locale].create_account.last_name}:&nbsp;
							</div>
							<div>{formData.lastName}</div>
						</div>
						<div className="flex mb-1 text-sm md:text-base">
							<div className="font-bold">
								{localeMap[locale].global.email}:&nbsp;
							</div>
							<div>{formData.emailAddress}</div>
						</div>
						<div className="flex mb-1 text-sm md:text-base">
							<div className="font-bold">
								{localeMap[locale].global.phone_number}:&nbsp;
							</div>
							<div>{formData.phoneNumber}</div>
						</div>
						<div className="flex mb-1 text-sm md:text-base">
							<div className="font-bold">
								{
									localeMap[locale].add_coborrower_form
										.married_civil_union
								}
								:&nbsp;
							</div>
							{formData.relationship === borrowerRelationships[1]
								? localeMap[locale].global.yes
								: localeMap[locale].global.no}
						</div>

						<div
							style={{ marginTop: 20, marginBottom: 20 }}
							className="text-sm md:text-base"
						>
							{localeMap[locale].add_coborrower_form.disclaimer.body}
						</div>
					</>
				)}
			</DialogContent>
			<DialogActions className="ml-5 mr-5">
				{(display === 'invite' ||
					display === 'relationship' ||
					display === 'submit') && (
					<Button
						text={localeMap[locale].global.go_back}
						variant={
							display === 'invite' ||
							display === 'relationship' ||
							display === 'submit'
								? 'outlined'
								: 'contained'
						}
						onClick={() => onBack()}
					/>
				)}
				{(display === 'method' ||
					display === 'borrowerLimitReached') && (
					<Button
						text={localeMap[locale].global.close}
						variant="contained"
						onClick={handleClose}
					/>
				)}
				{display === 'invite' && (
					<Button
						text={localeMap[locale].global.continue}
						disabled={
							!formData.firstName ||
							!formData.lastName ||
							!isEmailValid(formData.emailAddress) ||
							!formData.phoneNumber ||
							!isPhoneNumberValid(formData.phoneNumber) ||
							!!validationMessages.firstName ||
							!!validationMessages.lastName ||
							!!validationMessages.emailAddress ||
							!!validationMessages.phoneNumber
						}
						onClick={() => setDisplay('relationship')}
					/>
				)}
				{display === 'submit' && (
					<LoadingBtn
						fullWidth={false}
						text={
							localeMap[locale].add_coborrower_form.send_invitation
						}
						disabled={sending}
						loading={sending}
						onClick={sendInvite}
					/>
				)}
			</DialogActions>
		</Dialog>
	)
}

export default ModalAddCoBorrower
